import { classNames } from "helpers/ClassNameHelper";
import * as React from "react";

type HTMLInputTypeAttribute =
    | "button"
    | "checkbox"
    | "color"
    | "date"
    | "datetime-local"
    | "email"
    | "file"
    | "hidden"
    | "image"
    | "month"
    | "number"
    | "password"
    | "radio"
    | "range"
    | "reset"
    | "search"
    | "submit"
    | "tel"
    | "text"
    | "time"
    | "url"
    | "week"
    | (string & {});

type IInputBase = {
    size: "small" | "medium" | "large";
    name: string;
    className?: string;
    id: string;
    type: HTMLInputTypeAttribute;
    isValid?: boolean;
    isDisabled?: boolean;
    placeholder?: string;
    label?: string;
    errorMessage?: React.ReactNode;
    rightIcon?: React.ReactNode;
    leftIcon?: React.ReactNode;
    value?: any;
    readOnly?: boolean;
    flexField?: boolean;
    children?: React.ReactNode;
};

type PropsTextArea = {
    isArea: true;
    rows?: number;
    resizable?: boolean;
    onChange?: (event: React.ChangeEvent<HTMLTextAreaElement>) => void;
    onBlur?: (event: React.FocusEvent<HTMLTextAreaElement>) => void;
    onFocus?: (event: React.FocusEvent<HTMLTextAreaElement>) => void;
    onKeyPress?: (event: React.KeyboardEvent<HTMLTextAreaElement>) => void;
} & IInputBase;

export type PropsInput = {
    isArea?: false;
    onChange?: (event: React.ChangeEvent<HTMLInputElement>) => void;
    onBlur?: (event: React.FocusEvent<HTMLInputElement>) => void;
    onFocus?: (event: React.FocusEvent<HTMLInputElement>) => void;
    onKeyPress?: (event: React.KeyboardEvent<HTMLInputElement>) => void;
    max?: number;
    min?: number;
} & IInputBase;

export type IInput = PropsTextArea | PropsInput;

export const Input = React.forwardRef<HTMLInputElement, React.PropsWithChildren<IInput>>(
    (props: IInput, ref) => {
        const {
            size,
            name,
            className,
            id,
            type,
            isValid = true,
            isDisabled,
            placeholder,
            label,
            errorMessage,
            rightIcon,
            leftIcon,
            value,
            readOnly,
            flexField,
            children,
        } = props;

        const getInputSize = () => {
            return `input-container--${size}`;
        };

        const commonProps = {
            id,
            className: classNames(
                "form-input",
                !!leftIcon && "form-input-space-left",
                !!rightIcon && "form-input-space-right",
                !isValid && "form-input--error",
                className,
                isDisabled && "form-input--disabled"
            ),
            value,
            name,
            readOnly,
            disabled: isDisabled,
            placeholder,
        };

        return (
            <>
                <div
                    className={classNames(`input-container ${getInputSize()}`, flexField && "f-1")}
                >
                    {label ? <label htmlFor={id}>{label}</label> : null}
                    {props.isArea ? (
                        <textarea
                            {...commonProps}
                            className={classNames(
                                commonProps.className,
                                props.resizable && "resizable"
                            )}
                            onChange={props.onChange}
                            onBlur={props.onBlur}
                            onFocus={props.onFocus}
                            rows={props.rows ?? 3}
                            onKeyPress={props.onKeyPress}
                        />
                    ) : (
                        <>
                            {leftIcon && (
                                <div className="input-icon-container input-icon-left">
                                    <div
                                        className={classNames(
                                            "input-icon",
                                            isDisabled && "input-icon--disabled"
                                        )}
                                    >
                                        {leftIcon}
                                    </div>
                                </div>
                            )}
                            <input
                                {...commonProps}
                                type={type}
                                onChange={props.onChange}
                                onBlur={props.onBlur}
                                onFocus={props.onFocus}
                                min={props.min}
                                max={props.max}
                                onKeyPress={props.onKeyPress}
                                ref={ref}
                            />
                            {rightIcon && (
                                <div className="input-icon-container input-icon-right">
                                    <div
                                        className={classNames(
                                            "input-icon",
                                            isDisabled && "input-icon--disabled"
                                        )}
                                    >
                                        {rightIcon}
                                    </div>
                                </div>
                            )}
                        </>
                    )}
                    {children}
                </div>
                {!isValid && errorMessage ? (
                    <div className="error-message">{errorMessage}</div>
                ) : null}
            </>
        );
    }
);
